// «پیش‌فاکتور»: invoice phase 0. An offer listed among the sales invoices, never sent, that becomes an
// invoice only through «تبدیل به صورتحساب». The server owns every rule; this file only names and shows.

export const PROFORMA_PHASE_ID = 0

/** The «prf» object the server adds to a proforma row (InvoiceProformaDataReturn). */
export type InvoiceProformaInfo = {
  /** Last valid day, Jalali (1405/07/30). */
  vu: string
  /** Last valid day, Gregorian (2026-10-21). */
  vug: string
  /** Days left after today (Tehran); 0 on the last valid day, negative once expired. */
  dl: number
  /** open, expired or converted. */
  st: InvoiceProformaStateKey
  stt: string
  /** Open and close to its end. */
  exs: boolean
  /** Payment terms. */
  pt?: string | null
  /** Delivery terms. */
  dt?: string | null
  /** The invoice the proforma became: its hash and number. */
  cvh?: string
  cvn?: string
}

export type InvoiceProformaStateKey = 'open' | 'expired' | 'converted'

export const PROFORMA_KIND_OPTIONS = [
  {label: 'همهٔ اسناد', value: ''},
  {label: 'فقط صورتحساب‌ها', value: 'invoice'},
  {label: 'فقط پیش‌فاکتورها', value: 'proforma'},
]

export const PROFORMA_KIND_LABELS: Record<string, string> = {
  invoice: 'فقط صورتحساب‌ها',
  proforma: 'فقط پیش‌فاکتورها',
}

/** «وضعیت اعتبار» of the list filter; «رو به انقضا» is an open proforma in its last days. */
export const PROFORMA_STATE_OPTIONS = [
  {label: 'معتبر', value: 'open'},
  {label: 'رو به انقضا', value: 'expiring'},
  {label: 'منقضی‌شده', value: 'expired'},
  {label: 'تبدیل‌شده به صورتحساب', value: 'converted'},
]

export const PROFORMA_STATE_LABELS: Record<string, string> = Object.fromEntries(
  PROFORMA_STATE_OPTIONS.map((option) => [option.value, option.label])
)

type PhaseHolder = {ph?: {i?: number} | null} | null | undefined

export const isProformaInvoice = (invoice: PhaseHolder) => invoice?.ph?.i === PROFORMA_PHASE_ID

/**
 * A new document is a proforma only when «پیش‌فاکتور جدید» opened the form (user decision
 * 2026-10-13). Every other way in («صورتحساب جدید», a correction, return or cancellation, an
 * edit) registers an invoice, phase 1, whatever its pattern.
 */
export const opensNewProforma = (form: {
  /** Set by «پیش‌فاکتور جدید» only, and cleared when the form closes. */
  newProforma?: boolean
  /** null for a new document; a hash or id when an invoice is edited or referred to. */
  itemIdForUpdate?: unknown
  /** The referral the form was opened for (correction, return, cancellation…), if any. */
  referralAction?: unknown
}) =>
  form.newProforma === true &&
  form.itemIdForUpdate === null &&
  (form.referralAction === undefined || form.referralAction === null)

/** The line under a proforma's badge: until when it holds, or what became of it. */
export const describeProformaValidity = (proforma?: InvoiceProformaInfo | null) => {
  if (!proforma) {
    return ''
  }

  if (proforma.st === 'converted') {
    return proforma.cvn ? `تبدیل‌شده به صورتحساب ${proforma.cvn}` : 'تبدیل‌شده به صورتحساب'
  }

  if (proforma.st === 'expired') {
    return `منقضی‌شده (اعتبار تا ${proforma.vu})`
  }

  if (proforma.dl === 0) {
    return `معتبر تا امروز (${proforma.vu})`
  }

  return `معتبر تا ${proforma.vu} (${proforma.dl} روز مانده)`
}

/** Bootstrap colour of the validity line. */
export const proformaValidityTone = (proforma?: InvoiceProformaInfo | null) => {
  if (!proforma) return 'muted'
  if (proforma.st === 'converted') return 'success'
  if (proforma.st === 'expired') return 'danger'
  return proforma.exs ? 'warning' : 'info'
}

export const canConvertProforma = (proforma?: InvoiceProformaInfo | null) => proforma?.st === 'open'

/** Edit and «تمدید اعتبار»: every proforma that did not become an invoice. */
export const canChangeProforma = (proforma?: InvoiceProformaInfo | null) =>
  Boolean(proforma) && proforma?.st !== 'converted'
